import React, { useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
export interface OnboardingState {
  hasProfile: boolean;
  hasInterests: boolean;
  hasProject: boolean;
  completedAt: string | null;
  skippedSteps: string[];
}
interface Ctx {
  state: OnboardingState;
  setProfile: (v: boolean) => void;
  setInterests: (v: boolean) => void;
  setProject: (v: boolean) => void;
  markSkipped: (step: string) => void;
  markCompleted: () => void;
  reset: () => void;
}
const STORAGE_KEY = 'eiis.onboardingState';
const DEFAULT_STATE: OnboardingState = {
  hasProfile: false,
  hasInterests: false,
  hasProject: false,
  completedAt: null,
  skippedSteps: []
};
const OnboardingContext = createContext<Ctx | null>(null);
function loadInitial(): OnboardingState {
  if (typeof window === 'undefined') return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_STATE,
      ...parsed
    };
  } catch {
    return DEFAULT_STATE;
  }
}
export function OnboardingProvider({
  children
}: {
  children: ReactNode;
}) {
  const [state, setState] = useState<OnboardingState>(loadInitial);
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // fail silently
    }
  }, [state]);
  const setProfile = useCallback((v: boolean) => {
    setState(prev => ({
      ...prev,
      hasProfile: v
    }));
  }, []);
  const setInterests = useCallback((v: boolean) => {
    setState(prev => ({
      ...prev,
      hasInterests: v
    }));
  }, []);
  const setProject = useCallback((v: boolean) => {
    setState(prev => ({
      ...prev,
      hasProject: v
    }));
  }, []);
  const markSkipped = useCallback((step: string) => {
    setState(prev => prev.skippedSteps.includes(step) ? prev : {
      ...prev,
      skippedSteps: [...prev.skippedSteps, step]
    });
  }, []);
  const markCompleted = useCallback(() => {
    setState(prev => ({
      ...prev,
      completedAt: new Date().toISOString()
    }));
  }, []);
  const reset = useCallback(() => {
    setState(DEFAULT_STATE);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // fail silently
    }
  }, []);
  return <OnboardingContext.Provider value={{
    state,
    setProfile,
    setInterests,
    setProject,
    markSkipped,
    markCompleted,
    reset
  }} data-id="element-156">
      {children}
    </OnboardingContext.Provider>;
}
export function useOnboarding(): Ctx {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return ctx;
}